import { motion } from "framer-motion";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import type { LegalDocument } from "../../legal/legal.types";
import { LRNSY_LEGAL } from "../../legal/legal.constants";

export function LegalDocumentPage({ document }: { document: LegalDocument }) {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#040508] text-white selection:bg-amber-300 selection:text-black">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,rgba(245,158,11,0.13),transparent_36%),radial-gradient(circle_at_80%_20%,rgba(244,63,94,0.08),transparent_34%),linear-gradient(180deg,rgba(255,255,255,0.04),transparent_28%)]" />
      </div>

      <main className="relative z-10 mx-auto w-full max-w-5xl px-4 py-8 md:px-6 md:py-12">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-8 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-[10px] font-black uppercase tracking-[0.24em] text-white/60 transition hover:bg-white/10 hover:text-white"
        >
          <ArrowLeft size={14} />
          Back
        </button>

        <motion.header
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="overflow-hidden rounded-[34px] border border-white/10 bg-white/[0.045] p-6 shadow-[0_30px_100px_rgba(0,0,0,0.45)] backdrop-blur-xl md:p-9"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-300/20 bg-amber-300/10 px-4 py-2 text-amber-100">
            <ShieldCheck size={14} />
            <span className="text-[9px] font-black uppercase tracking-[0.3em]">LRNSY Legal</span>
          </div>

          <h1 className="mt-6 text-4xl font-light tracking-[-0.06em] md:text-6xl">
            {document.title}
          </h1>

          <p className="mt-5 max-w-3xl text-sm leading-7 text-white/58 md:text-base md:leading-8">
            {document.subtitle}
          </p>

          <div className="mt-7 grid gap-3 text-xs text-white/45 sm:grid-cols-2 md:grid-cols-4">
            <Meta label="Effective" value={document.effectiveDate} />
            <Meta label="Updated" value={document.lastUpdated} />
            <Meta label="Operator" value={LRNSY_LEGAL.operator} />
            <Meta label="Jurisdiction" value={LRNSY_LEGAL.jurisdiction} />
          </div>
        </motion.header>

        <div className="mt-6 grid gap-4 md:grid-cols-[240px_1fr]">
          <aside className="hidden md:block">
            <div className="sticky top-6 rounded-[28px] border border-white/10 bg-white/[0.035] p-4 backdrop-blur-xl">
              <p className="px-2 text-[9px] font-black uppercase tracking-[0.28em] text-white/35">
                Sections
              </p>

              <nav className="mt-4 space-y-1">
                {document.sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="block rounded-xl px-3 py-2 text-xs leading-5 text-white/50 transition hover:bg-white/10 hover:text-white"
                  >
                    {section.title}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          <section className="space-y-4">
            {document.sections.map((section, index) => (
              <motion.article
                key={section.id}
                id={section.id}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.35, delay: Math.min(index * 0.02, 0.16) }}
                className="scroll-mt-8 rounded-[28px] border border-white/10 bg-white/[0.035] p-5 backdrop-blur-xl md:p-7"
              >
                {section.eyebrow && (
                  <p className="mb-2 text-[9px] font-black uppercase tracking-[0.28em] text-amber-200/50">
                    {section.eyebrow}
                  </p>
                )}

                <h2 className="text-2xl font-light tracking-[-0.045em] text-white/95">
                  {section.title}
                </h2>

                <div className="mt-4 space-y-4">
                  {section.body.map((paragraph) => (
                    <p key={paragraph} className="text-sm leading-7 text-white/60">
                      {paragraph}
                    </p>
                  ))}
                </div>

                {section.bullets && (
                  <ul className="mt-5 grid gap-2">
                    {section.bullets.map((bullet) => (
                      <li
                        key={bullet}
                        className="rounded-2xl border border-white/8 bg-black/20 px-4 py-3 text-sm leading-6 text-white/58"
                      >
                        {bullet}
                      </li>
                    ))}
                  </ul>
                )}

                {section.note && (
                  <div className="mt-5 rounded-2xl border border-amber-300/15 bg-amber-300/[0.07] px-4 py-3 text-sm leading-6 text-amber-50/75">
                    {section.note}
                  </div>
                )}
              </motion.article>
            ))}

            <div className="rounded-[28px] border border-white/10 bg-black/30 p-6 text-sm leading-7 text-white/50">
              For questions, contact{" "}
              <a
                className="text-white underline decoration-white/20 underline-offset-4"
                href={`mailto:${LRNSY_LEGAL.supportEmail}`}
              >
                {LRNSY_LEGAL.supportEmail}
              </a>
              . You can also manage privacy and cookie choices from{" "}
              <Link
                className="text-white underline decoration-white/20 underline-offset-4"
                to="/settings/privacy"
              >
                Privacy Settings
              </Link>
              .
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3">
      <p className="text-[8px] font-black uppercase tracking-[0.26em] text-white/30">{label}</p>
      <p className="mt-1 text-white/70">{value}</p>
    </div>
  );
}
